import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Avatar,
  TextField,
  Button,
  CircularProgress
} from '@mui/material';
import {
  Description as DraftIcon,
  CheckCircle as ApprovedIcon,
  Groups as TotalQuotesIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import moment from 'moment';
import { DateInputField } from 'components';
import { downloadQuoteAnalyticsReport, getQuoteDetailsForAnalyticReport } from 'services';
import { DashboardBarChartCard } from 'modules/quote/Dashboard/components/DashboardBarChartCard';
import { ClientMultiSelect, ClientOption } from 'modules/quote/Dashboard/components/ClientMultiSelect';

const toClientIds = (value: string) => value.split(',').map((id) => id.trim()).filter(Boolean);

interface QuoteAnalyticsItem {
  id: number;
  clientName?: string;
  createdByName?: string;
  createdOn?: string;
  statusName?: string;
  totalQuoteValue?: number;
}

interface StatCardProps {
  title: string;
  count: number;
  color: string;
}

const getStatCardIcon = (title: string) => {
  const normalizedTitle = String(title || '').trim().toLowerCase();

  if (normalizedTitle === 'draft items') {
    return <DraftIcon sx={{ fontSize: 30 }} />;
  }

  if (normalizedTitle === 'approved') {
    return <ApprovedIcon sx={{ fontSize: 30 }} />;
  }

  return <TotalQuotesIcon sx={{ fontSize: 30 }} />;
};

const StatCard: React.FC<StatCardProps> = ({ title, count, color }) => (
  <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
            {count.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Avatar
          sx={{
            bgcolor: color,
            width: 56,
            height: 56,
            boxShadow: '0 4px 14px 0 rgba(0,0,0,0.15)'
          }}
        >
          {getStatCardIcon(title)}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

export function QuotesPage() {
  const today = moment().startOf('day').format('YYYY-MM-DD');
  const defaultFromDate = moment().startOf('day').subtract(6, 'days').format('YYYY-MM-DD');

  const initialFormData = {
    fromDate: defaultFromDate,
    toDate: today,
    clientName: '',
    createdBy: ''
  };

  const [formData, setFormData] = useState(initialFormData);
  const [quotes, setQuotes] = useState<QuoteAnalyticsItem[]>([]);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const isSearchingRef = useRef(false);
  const [selectedClients, setSelectedClients] = useState<ClientOption[]>([]);

  const handleClientChange = useCallback((clients: ClientOption[]) => {
    setSelectedClients(clients);
    setFormData((prev) => ({ ...prev, clientName: clients.map((client) => client.id).join(',') }));
  }, []);

  const extractDownloadFileName = (contentDisposition?: string) => {
    if (!contentDisposition) {
      return 'QuotesAnalytics.xlsx';
    }

    const utf8NameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8NameMatch?.[1]) {
      return decodeURIComponent(utf8NameMatch[1].replace(/\"/g, '').trim());
    }

    const plainNameMatch = contentDisposition.match(/filename=([^;]+)/i);
    if (plainNameMatch?.[1]) {
      return plainNameMatch[1].replace(/\"/g, '').trim();
    }

    return 'QuotesAnalytics.xlsx';
  };

  const triggerFileDownload = (response: any) => {
    if (!response?.data) {
      return;
    }

    const contentType = response?.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const contentDisposition = response?.headers?.['content-disposition'];
    const fileName = extractDownloadFileName(contentDisposition);

    const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const fetchQuoteData = async (filters: typeof formData) => {
    const effectiveFromDate = filters.fromDate || defaultFromDate;
    const effectiveToDate = filters.toDate || today;
    const apiFromDate = moment(effectiveFromDate, 'YYYY-MM-DD', true).format('YYYY-MM-DD');
    const apiToDate = moment(effectiveToDate, 'YYYY-MM-DD', true).format('YYYY-MM-DD');

    setIsLoading(true);

    try {
      const response = await getQuoteDetailsForAnalyticReport(
        apiFromDate,
        apiToDate,
        filters.createdBy !== '' ? filters.createdBy : null,
        toClientIds(filters.clientName)
      );

      const payload = response?.data;
      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

      setQuotes(rows);
      setHasFetchedData(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchQuoteData(initialFormData);
  }, []);

  const handleSearch = async () => {
    if (isSearchingRef.current) return;
    isSearchingRef.current = true;
    setIsSearching(true);
    try {
      await fetchQuoteData(formData);
    } finally {
      isSearchingRef.current = false;
      setIsSearching(false);
    }
  };

  const handleDownloadReport = async () => {
    const effectiveFromDate = formData.fromDate || defaultFromDate;
    const effectiveToDate = formData.toDate || today;
    const apiFromDate = moment(effectiveFromDate, 'YYYY-MM-DD', true).format('YYYY-MM-DD');
    const apiToDate = moment(effectiveToDate, 'YYYY-MM-DD', true).format('YYYY-MM-DD');

    const response = await downloadQuoteAnalyticsReport(
      apiFromDate,
      apiToDate,
      formData.createdBy !== '' ? formData.createdBy : null,
      toClientIds(formData.clientName)
    );

    triggerFileDownload(response);
  };

  const handleClearAll = () => {
    setFormData(initialFormData);
    setSelectedClients([]);
    fetchQuoteData(initialFormData);
  };

  const hasDefaultDates = formData.fromDate === defaultFromDate && formData.toDate === today;
  const hasFilterChanges = Object.keys(formData).some((key) => {
    const typedKey = key as keyof typeof formData;
    return formData[typedKey] !== initialFormData[typedKey];
  });
  const isSearchEnabled = hasDefaultDates || hasFilterChanges;

  const filteredQuotes = quotes;

  const statusTotals = useMemo(() => {
    return filteredQuotes.reduce<Record<string, number>>((acc, item) => {
      const statusName = String(item.statusName || '').trim();
      if (!statusName) {
        return acc;
      }
      acc[statusName] = (acc[statusName] || 0) + 1;
      return acc;
    }, {});
  }, [filteredQuotes]);

  const asmCountData = useMemo(() => {
    const totals = filteredQuotes.reduce<Record<string, number>>((acc, item) => {
      const createdByName = String(item.createdByName || '').trim();
      if (!createdByName) {
        return acc;
      }
      acc[createdByName] = (acc[createdByName] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(totals).map(([createdByName, count]) => ({ createdByName, count }));
  }, [filteredQuotes]);

  const asmValueData = useMemo(() => {
    const totals = filteredQuotes.reduce<Record<string, number>>((acc, item) => {
      const createdByName = String(item.createdByName || '').trim();
      if (!createdByName) {
        return acc;
      }
      const quoteValue = Number(item.totalQuoteValue || 0);
      acc[createdByName] = (acc[createdByName] || 0) + (Number.isFinite(quoteValue) ? quoteValue : 0);
      return acc;
    }, {});

    return Object.entries(totals).map(([createdByName, totalQuoteValue]) => ({
      createdByName,
      totalQuoteValue: Number(totalQuoteValue.toFixed(2))
    }));
  }, [filteredQuotes]);

  const clientCountData = useMemo(() => {
    const totals = filteredQuotes.reduce<Record<string, number>>((acc, item) => {
      const clientName = String(item.clientName || '').trim();
      if (!clientName) {
        return acc;
      }
      acc[clientName] = (acc[clientName] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(totals).map(([clientName, count]) => ({ clientName, count }));
  }, [filteredQuotes]);

  const clientValueData = useMemo(() => {
    const totals = filteredQuotes.reduce<Record<string, number>>((acc, item) => {
      const clientName = String(item.clientName || '').trim();
      if (!clientName) {
        return acc;
      }
      const quoteValue = Number(item.totalQuoteValue || 0);
      acc[clientName] = (acc[clientName] || 0) + (Number.isFinite(quoteValue) ? quoteValue : 0);
      return acc;
    }, {});

    return Object.entries(totals).map(([clientName, totalQuoteValue]) => ({
      clientName,
      totalQuoteValue: Number(totalQuoteValue.toFixed(2))
    }));
  }, [filteredQuotes]);

  const renderNoDataFallback = (title: string) => (
    <Box
      sx={{
        p: 3,
        border: '1px solid #e0e0e0',
        borderRadius: '6px',
        minHeight: 350,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 2 }}>
        {title}
      </Typography>
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6">
          No data found.
        </Typography>
      </Box>
    </Box>
  );

  const renderLoadingFallback = () => (
    <Box
      sx={{
        p: 3,
        border: '1px solid #e0e0e0',
        borderRadius: '6px',
        minHeight: 350,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={32} />
        <Typography variant="h6">
          Loading dashboard data...
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      <Grid
        container
        component="form"
        spacing={2}
        sx={{ mb: 4, backgroundColor: '#f5f5f5', p: 2, borderRadius: '6px', width: '100%', ml: 0, mt: 0 }}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (isSearchEnabled) handleSearch();
        }}
      >
        <Grid item xs={12} sm={6} md={3}>
          <DateInputField
            fullWidth
            label="From Date"
            value={formData.fromDate}
            onChange={(value) => setFormData((prev) => ({ ...prev, fromDate: value }))}
            max={formData.toDate || today}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DateInputField
            fullWidth
            label="To Date"
            value={formData.toDate}
            onChange={(value) => setFormData((prev) => ({ ...prev, toDate: value }))}
            min={formData.fromDate}
            max={today}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ClientMultiSelect label="Client" value={selectedClients} onChange={handleClientChange} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Created By"
            size="small"
            value={formData.createdBy}
            onChange={(event) => setFormData((prev) => ({ ...prev, createdBy: event.target.value }))}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-start' }, gap: 1, width: '100%' }}>
            <Button type="button" variant="contained" disabled={!isSearchEnabled || isSearching} onClick={handleSearch}>Search</Button>
            <Button type="button" variant="outlined" onClick={handleClearAll}>Clear All</Button>
          </Box>
        </Grid>
      </Grid>

      {isLoading ? (
        renderLoadingFallback()
      ) : hasFetchedData && filteredQuotes.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center', border: '1px solid #ccc', borderRadius: '6px' }}>
          <Typography variant="h6">
            No data found.
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Draft Items"
                count={statusTotals.Draft || 0}
                color="#003883"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Approved"
                count={statusTotals.Approved || 0}
                color="#5765a6"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Total Quotes"
                count={filteredQuotes.length}
                color="#ee7d11"
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownloadReport}>
              Download Report
            </Button>
          </Box>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6} mb={2}>
              {asmCountData.length > 0 ? (
                <DashboardBarChartCard
                  title="ASM - Number of Quotes Created"
                  data={asmCountData}
                  xDataKey="createdByName"
                  barName="Quotes"
                  barColor="#003883"
                  barCategoryGap="20%"
                  xAxisAngle={-20}
                  xAxisHeight={80}
                />
              ) : renderNoDataFallback('ASM - Number of Quotes Created')}
            </Grid>

            <Grid item xs={12} md={6} mb={2}>
              {asmValueData.length > 0 ? (
                <DashboardBarChartCard
                  title="ASM - Sum of Quote Value"
                  data={asmValueData}
                  xDataKey="createdByName"
                  barDataKey="totalQuoteValue"
                  barName="Quote Value"
                  barColor="#5765a6"
                  barCategoryGap="20%"
                  xAxisAngle={-20}
                  xAxisHeight={80}
                />
              ) : renderNoDataFallback('ASM - Sum of Quote Value')}
            </Grid>

            <Grid item xs={12} md={6} mb={2}>
              {clientCountData.length > 0 ? (
                <DashboardBarChartCard
                  title="Client - Number of Quotes Created"
                  data={clientCountData}
                  xDataKey="clientName"
                  barName="Quotes"
                  barColor="#ee7d11"
                  barCategoryGap="20%"
                  xAxisAngle={-20}
                  xAxisHeight={80}
                />
              ) : renderNoDataFallback('Client - Number of Quotes Created')}
            </Grid>

            <Grid item xs={12} md={6} mb={2}>
              {clientValueData.length > 0 ? (
                <DashboardBarChartCard
                  title="Client - Sum of Quote Value"
                  data={clientValueData}
                  xDataKey="clientName"
                  barDataKey="totalQuoteValue"
                  barName="Quote Value"
                  barColor="#41a85f"
                  barCategoryGap="20%"
                  xAxisAngle={-20}
                  xAxisHeight={80}
                />
              ) : renderNoDataFallback('Client - Sum of Quote Value')}
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}
